import React, { useEffect, useState } from "react";
import Layout from "@/components/Layout";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Save, Lock, Target } from "lucide-react";
import StaffManager from "@/components/StaffManager";

export default function Profile() {
  const { user, setUser, refreshUser } = useAuth();
  const [specialties, setSpecialties] = useState([]);
  const [form, setForm] = useState({
    name: "", specialty: "", fellowship_program: "",
    fellowship_year: 1, pgy_level: "", board_target: 300,
  });
  const [pwd, setPwd] = useState({ current_password: "", new_password: "", confirm: "" });
  const [saving, setSaving] = useState(false);
  const [pwdSaving, setPwdSaving] = useState(false);
  const [pwdError, setPwdError] = useState("");

  useEffect(() => {
    api.get("/specialties").then(({ data }) => setSpecialties(data));
  }, []);

  useEffect(() => {
    if (user) {
      setForm({
        name: user.name || "",
        specialty: user.specialty || "",
        fellowship_program: user.fellowship_program || "",
        fellowship_year: user.fellowship_year || 1,
        pgy_level: user.pgy_level || "",
        board_target: user.board_target || 300,
      });
    }
  }, [user]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const saveProfile = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const { data } = await api.put("/profile", {
        ...form,
        fellowship_year: Number(form.fellowship_year),
        board_target: Number(form.board_target),
      });
      setUser(data);
      toast.success("Profile updated");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  const changePassword = async (e) => {
    e.preventDefault();
    setPwdError("");
    if (pwd.new_password !== pwd.confirm) return setPwdError("Passwords do not match");
    setPwdSaving(true);
    try {
      await api.put("/profile/password", {
        current_password: pwd.current_password,
        new_password: pwd.new_password,
      });
      toast.success("Password changed");
      setPwd({ current_password: "", new_password: "", confirm: "" });
      await refreshUser();
    } catch (err) {
      setPwdError(formatApiError(err));
    } finally {
      setPwdSaving(false);
    }
  };

  return (
    <Layout>
      <div className="mb-8">
        <h1 className="heading text-3xl md:text-4xl text-[#3F4E5B]">Profile</h1>
        <p className="subtle mt-2">Personal & fellowship information.</p>
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        <form onSubmit={saveProfile} className="neu-card p-6 md:p-8 space-y-5 lg:col-span-2" data-testid="profile-form">
          <div className="heading text-lg text-[#3F4E5B]">General</div>
          <div>
            <label className="text-sm font-medium block mb-2">Full name</label>
            <input required value={form.name} onChange={(e) => set("name", e.target.value)} className="neu-input" data-testid="profile-name" />
          </div>
          <div className="grid md:grid-cols-2 gap-5">
            <div>
              <label className="text-sm font-medium block mb-2">Specialty</label>
              <select value={form.specialty} onChange={(e) => set("specialty", e.target.value)} className="neu-input" data-testid="profile-specialty">
                <option value="">—</option>
                {specialties.map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
            <div>
              <label className="text-sm font-medium block mb-2">Fellowship year</label>
              <select value={form.fellowship_year} onChange={(e) => set("fellowship_year", Number(e.target.value))} className="neu-input" data-testid="profile-year">
                <option value={1}>Year 1</option>
                <option value={2}>Year 2</option>
              </select>
            </div>
            <div className="md:col-span-2">
              <label className="text-sm font-medium block mb-2">Fellowship program</label>
              <input value={form.fellowship_program} onChange={(e) => set("fellowship_program", e.target.value)} className="neu-input" data-testid="profile-program" />
            </div>
            <div>
              <label className="text-sm font-medium block mb-2">PGY level</label>
              <input value={form.pgy_level} onChange={(e) => set("pgy_level", e.target.value)} placeholder="PGY-6" className="neu-input" data-testid="profile-pgy" />
            </div>
            <div>
              <label className="text-sm font-medium block mb-2">
                <Target className="w-3.5 h-3.5 inline mr-1 text-neu-accent" />
                Board target (cases)
              </label>
              <input type="number" min={0} value={form.board_target} onChange={(e) => set("board_target", e.target.value)} className="neu-input" data-testid="profile-board" />
            </div>
          </div>
          <div className="flex justify-end pt-2">
            <button type="submit" disabled={saving} className="btn-accent inline-flex items-center gap-2" data-testid="profile-save">
              <Save className="w-4 h-4" />
              {saving ? "Saving…" : "Save profile"}
            </button>
          </div>
        </form>

        <form onSubmit={changePassword} className="neu-card p-6 md:p-8 space-y-5" data-testid="password-form">
          <div className="flex items-center gap-2">
            <Lock className="w-5 h-5 text-neu-accent" />
            <div className="heading text-lg text-[#3F4E5B]">Change password</div>
          </div>
          <div>
            <label className="text-sm font-medium block mb-2">Current password</label>
            <input required type="password" value={pwd.current_password} onChange={(e) => setPwd({ ...pwd, current_password: e.target.value })} className="neu-input" data-testid="pwd-current" />
          </div>
          <div>
            <label className="text-sm font-medium block mb-2">New password</label>
            <input required type="password" minLength={6} value={pwd.new_password} onChange={(e) => setPwd({ ...pwd, new_password: e.target.value })} className="neu-input" data-testid="pwd-new" />
          </div>
          <div>
            <label className="text-sm font-medium block mb-2">Confirm new password</label>
            <input required type="password" minLength={6} value={pwd.confirm} onChange={(e) => setPwd({ ...pwd, confirm: e.target.value })} className="neu-input" data-testid="pwd-confirm" />
          </div>
          {pwdError && <div className="text-sm text-red-600 bg-red-50 p-3 rounded-xl">{pwdError}</div>}
          <button type="submit" disabled={pwdSaving} className="btn-accent w-full" data-testid="pwd-save">
            {pwdSaving ? "Updating…" : "Update password"}
          </button>
        </form>
      </div>

      <div className="mt-6">
        <StaffManager />
      </div>
    </Layout>
  );
}
